import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  FaFileAlt,
  FaTrash,
  FaPlus,
  FaListOl,
  FaSave,
  FaEye,
  FaSearch,
  FaPlusCircle,
  FaRobot,
} from "react-icons/fa";
import api from "../utils/api";
import { useNavigate } from "react-router-dom";

// 🧩 استخراج المتغيرات من النص ({{...}})
const extractVariables = (text) => {
  const matches = text.match(/{{\s*[\u0621-\u064A\w_]+\s*}}/g) || [];
  return matches.map((m) => m.replace(/[{}]/g, "").trim());
};

// 🔍 اكتشاف المتغيرات من النص العربي بدون أقواس
const detectArabicVariables = (text) => {
  const pattern =
    /(اسم|تاريخ|المبلغ|العنوان|المدة|القيمة|الطرف|العقد|الشاهد)\s+[اأإآa-zA-Z\u0621-\u064A_]+/g;
  const matches = text.match(pattern) || [];
  return matches.map((m) => `{{${m.replace(/\s+/g, "_")}}}`);
};

function TemplatesManager() {
  const [templates, setTemplates] = useState([]);
  const [newTemplate, setNewTemplate] = useState({ name: "", description: "" });
  const [sections, setSections] = useState([{ title: "مقدمة", content: "" }]);
  const [fields, setFields] = useState([]);
  const [previewHTML, setPreviewHTML] = useState("");
  const [aiPrompt, setAiPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // 🧭 تحميل العقود
  const fetchTemplates = async () => {
    try {
      const res = await api.get("/templates");
      setTemplates(res.data);
    } catch (err) {
      console.error("❌ فشل تحميل العقود:", err);
    }
  };
  useEffect(() => {
    fetchTemplates();
  }, []);

  // ➕ إضافة بند
  const addSection = () => setSections([...sections, { title: "", content: "" }]);
  const removeSection = (i) => setSections(sections.filter((_, idx) => idx !== i));

  // ✏️ تحديث بند
  const updateSection = (i, key, value) => {
    const copy = [...sections];
    copy[i][key] = value;
    setSections(copy);
  };

  // 🧠 اكتشاف المتغيرات تلقائيًا
  const discoverVariables = () => {
    const allText = sections.map((s) => s.content).join(" ");
    const autoVars = detectArabicVariables(allText);
    const explicitVars = extractVariables(allText);
    const unique = [...new Set([...autoVars, ...explicitVars])];

    const formatted = unique.map((v) => ({
      key: v.replace(/[{}]/g, "").trim(),
      label: v.replace(/[{}]/g, "").trim().replace(/_/g, " "),
      type: "text",
    }));
    setFields(formatted);
    alert(`✅ تم اكتشاف ${formatted.length} متغيرًا بنجاح!`);
  };

  // 💾 حفظ النموذج
  const saveTemplate = async () => {
    if (!newTemplate.name.trim()) return alert("يرجى إدخال اسم العقد.");
    const bodyText = sections
      .map(
        (s, i) => `<h3>${i + 1}. ${s.title || "بند"}</h3><p>${s.content || ""}</p>`
      )
      .join("\n");

    try {
      await api.post("/templates", { ...newTemplate, bodyText, fields });
      alert("✅ تم حفظ العقد بنجاح!");
      setNewTemplate({ name: "", description: "" });
      setSections([{ title: "مقدمة", content: "" }]);
      setFields([]);
      fetchTemplates();
    } catch (err) {
      console.error("❌ خطأ أثناء الحفظ:", err);
      alert("حدث خطأ أثناء الحفظ.");
    }
  };

  // 🤖 توليد عقد بالذكاء الصناعي من وصف المحامي والمتغيرات اليدوية
  const generateAIContract = async () => {
    if (!aiPrompt.trim()) return alert("يرجى إدخال وصف أو ملخص العقد.");
    setLoading(true);
    try {
      const variablesText = fields.map((f) => f.label).join(", ");
      const res = await api.post("/templates/generate-contract", {
        prompt: `الوصف: ${aiPrompt}\nالمتغيرات المطلوبة: ${variablesText}`,
      });

      const aiContract = res.data.bodyText;
      const vars = extractVariables(aiContract);

      setSections([{ title: "العقد", content: aiContract }]);
      setFields(vars.map((v) => ({ key: v, label: v, type: "text" })));
      alert("✅ تم توليد العقد بالذكاء الصناعي بنجاح!");
    } catch (err) {
      console.error(err);
      alert("❌ فشل توليد العقد بالذكاء الصناعي.");
    } finally {
      setLoading(false);
    }
  };

  // 👁️ معاينة فورية
  useEffect(() => {
    const html = `
      <div style="direction: rtl; font-family: 'Cairo', sans-serif; color:#111; background:#fff;
      padding:40px; border-radius:12px; line-height:2; box-shadow:0 0 10px rgba(0,0,0,0.15);">
        <h1 style="text-align:center; font-size:24px; font-weight:bold; margin-bottom:20px;">
          ${newTemplate.name || "نموذج عقد"}
        </h1>
        ${sections
          .map(
            (s, i) => `
          <h3 style="color:#222;">${i + 1}. ${s.title || "بند"}</h3>
          <p>${s.content || ""}</p>`
          )
          .join("")}
      </div>`;
    setPreviewHTML(html);
  }, [sections, newTemplate.name]);

  // 🧩 إدارة المتغيرات
  const updateField = (i, key, val) => {
    const copy = [...fields];
    copy[i][key] = val;
    setFields(copy);
  };
  const addField = () => setFields([...fields, { key: "", label: "", type: "text" }]);
  const removeField = (i) => setFields(fields.filter((_, idx) => idx !== i));

  // 🗑️ حذف عقد محفوظ
  const deleteTemplate = async (id) => {
    if (!window.confirm("هل تريد حذف هذا العقد؟")) return;
    try {
      await api.delete(`/templates/${id}`);
      fetchTemplates();
    } catch (err) {
      alert("❌ فشل الحذف.");
    }
  };

  return (
    <div className="p-8 bg-gray-900 text-white min-h-screen">
      <h1 className="text-3xl font-extrabold text-yellow-400 mb-8 text-center">
        ⚖️ صانع العقود القانونية الذكي – مع دعم الذكاء الصناعي والمتغيرات
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 max-w-7xl mx-auto">
        {/* ✍️ الإنشاء */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gray-800 p-6 rounded-xl shadow-lg"
        >
          <input
            placeholder="📝 اسم العقد (مثلاً: عقد إيجار أو بيع)"
            value={newTemplate.name}
            onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
            className="p-2 w-full rounded text-black mb-4"
          />

          {sections.map((sec, i) => (
            <div key={i} className="bg-gray-700 p-4 rounded-lg mb-3 relative">
              <h3 className="text-lg text-yellow-300 mb-2 flex items-center gap-2">
                <FaListOl /> البند {i + 1}
              </h3>
              <input
                placeholder="عنوان البند"
                value={sec.title}
                onChange={(e) => updateSection(i, "title", e.target.value)}
                className="p-2 rounded w-full text-black mb-2"
              />
              <textarea
                placeholder="اكتب نص البند هنا..."
                value={sec.content}
                onChange={(e) => updateSection(i, "content", e.target.value)}
                rows="4"
                className="p-2 rounded w-full text-black"
              />
              {sections.length > 1 && (
                <button
                  onClick={() => removeSection(i)}
                  className="absolute top-2 left-2 bg-red-500 hover:bg-red-400 text-black px-2 py-1 rounded text-sm"
                >
                  🗑️
                </button>
              )}
            </div>
          ))}

          <button
            onClick={addSection}
            className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold px-3 py-2 rounded-lg mt-4 flex items-center gap-2"
          >
            <FaPlus /> بند جديد
          </button>

          <button
            onClick={discoverVariables}
            className="bg-blue-500 hover:bg-blue-400 text-white font-bold p-3 rounded-lg mt-6 w-full flex items-center justify-center gap-2"
          >
            <FaSearch /> اكتشف المتغيرات تلقائيًا
          </button>

          {/* 🧠 AI توليد عقد */}
          <div className="bg-gray-700 p-4 rounded-lg mt-6">
            <h3 className="text-yellow-300 text-lg mb-2 flex items-center gap-2">
              <FaRobot /> توليد عقد بالذكاء الصناعي
            </h3>
            <textarea
              placeholder="اكتب وصفًا للعقد المطلوب (مثلاً: عقد شراكة بين شركتين لمدة سنتين...)"
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              rows="3"
              className="p-3 w-full rounded text-black mb-3"
            />
            <button
              onClick={generateAIContract}
              disabled={loading}
              className="w-full bg-purple-500 hover:bg-purple-400 text-white font-bold px-4 py-2 rounded-lg transition"
            >
              {loading ? "⏳ جاري إنشاء العقد..." : "🚀 توليد عقد تلقائي من الملخص"}
            </button>
          </div>

          <button
            onClick={saveTemplate}
            className="bg-green-500 hover:bg-green-400 text-black font-bold p-3 rounded-lg mt-4 w-full flex items-center justify-center gap-2"
          >
            <FaSave /> حفظ العقد
          </button>
        </motion.div>

        {/* 👁️ المعاينة + المتغيرات */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-gray-800 p-6 rounded-xl shadow-xl overflow-y-auto max-h-[80vh]"
        >
          <h2 className="text-yellow-300 text-xl mb-4 flex items-center gap-2 justify-center">
            <FaEye /> المعاينة الفورية
          </h2>
          <div
            className="bg-white text-black p-6 rounded-lg shadow-lg"
            style={{
              direction: "rtl",
              fontFamily: "Cairo, sans-serif",
              lineHeight: "2",
              fontSize: "16px",
              border: "1px solid #ddd",
            }}
            dangerouslySetInnerHTML={{ __html: previewHTML }}
          ></div>

          <h3 className="text-yellow-300 text-lg mt-6 mb-3 flex items-center gap-2">
            <FaPlusCircle /> المتغيرات المكتشفة / اليدوية
          </h3>
          {fields.length === 0 ? (
            <p className="text-gray-400">لم يتم اكتشاف متغيرات بعد.</p>
          ) : (
            fields.map((f, i) => (
              <div key={i} className="bg-gray-700 p-3 rounded-lg mb-2">
                <input
                  type="text"
                  value={f.key}
                  placeholder="اسم المتغير"
                  onChange={(e) => updateField(i, "key", e.target.value)}
                  className="p-2 w-full text-black rounded mb-2"
                />
                <input
                  type="text"
                  value={f.label}
                  placeholder="العنوان الظاهر للمستخدم"
                  onChange={(e) => updateField(i, "label", e.target.value)}
                  className="p-2 w-full text-black rounded mb-2"
                />
                <select
                  value={f.type}
                  onChange={(e) => updateField(i, "type", e.target.value)}
                  className="p-2 w-full text-black rounded"
                >
                  <option value="text">نص</option>
                  <option value="number">رقم</option>
                  <option value="date">تاريخ</option>
                  <option value="textarea">فقرة</option>
                  <option value="signature">توقيع</option>
                </select>
                <button
                  onClick={() => removeField(i)}
                  className="mt-2 bg-red-500 hover:bg-red-400 text-black px-3 py-1 rounded text-sm"
                >
                  🗑️ حذف
                </button>
              </div>
            ))
          )}
          <button
            onClick={addField}
            className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold px-3 py-2 rounded-lg mt-3 w-full flex items-center justify-center gap-2"
          >
            <FaPlus /> إضافة متغير يدويًا
          </button>
        </motion.div>
      </div>

      {/* 📚 العقود المحفوظة */}
      <div className="max-w-6xl mx-auto mt-16">
        <h2 className="text-2xl font-bold text-yellow-400 mb-4 text-center">
          📂 العقود المحفوظة
        </h2>
        {templates.length === 0 ? (
          <p className="text-gray-400 text-center">لا توجد عقود محفوظة بعد.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {templates.map((t) => (
              <motion.div
                key={t._id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-gray-800 p-5 rounded-lg shadow-md hover:shadow-yellow-400/30 transition"
              >
                <h3 className="text-xl font-bold text-yellow-300 mb-2 flex items-center gap-2">
                  <FaFileAlt /> {t.name}
                </h3>
                <div className="flex justify-between">
                  <button
                    onClick={() => navigate(`/generate/${t._id}`)}
                    className="bg-blue-500 hover:bg-blue-400 text-black font-bold px-3 py-1 rounded transition"
                  >
                    📄 استخدم
                  </button>
                  <button
                    onClick={() => deleteTemplate(t._id)}
                    className="bg-red-500 hover:bg-red-400 text-black font-bold px-3 py-1 rounded transition"
                  >
                    🗑️ حذف
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default TemplatesManager;
